import React, { PureComponent } from "react";
import { bool, func, string, object, oneOfType, oneOf } from "prop-types";
import ReactDatePicker, { registerLocale } from "react-datepicker";
import sv from "date-fns/locale/sv";
import Moment from "../../../modules/moment.module";
import "react-datepicker/dist/react-datepicker.min.css";

registerLocale("sv", sv);

class DatePicker extends PureComponent {
  state = {
    rawInputValue: null,
  };

  static propTypes = {
    disabled: bool,
    fieldName: string,
    isLoading: bool,
    error: bool,
    icon: string,
    muted: bool,
    onClose: func,
    onChange: func,
    onOpen: func,
    placeholder: string,
    showWeekNumbers: bool,
    minDate: string,
    maxDate: string,
    timepickerOnly: bool,
    showTimeSelect: bool,
    value: oneOfType([string, object]),
    calendarPlacement: oneOf([
      "bottom-start",
      "bottom-end",
      "top-start",
      "top-end",
    ]),
    isClearable: bool,
    hasError: bool,
    showTimeSelectOnly: bool,
    language: string,
    onBlur: func,
  };

  static defaultProps = {
    isLoading: false,
    showWeekNumbers: true,
    disabled: false,
    timepickerOnly: false,
    showTimeSelect: false,
    calendarPlacement: "bottom-start",
    showTimeSelectOnly: false,
  };

  componentDidUpdate(prevProps) {
    const { showTimeSelectOnly, value } = this.props;

    if (
      showTimeSelectOnly &&
      this.state.rawInputValue !== null &&
      value !== prevProps.value
    ) {
      this.setState({ rawInputValue: null });
    }
  }

  onChange = (val) => {
    const { onChange, fieldName } = this.props;
    const dateObj = Moment(val).toISOString();

    if (fieldName) {
      onChange && onChange(fieldName, dateObj);
      return;
    }

    onChange && onChange(dateObj);
  };

  filterDate = (date) => {
    const { minDate, maxDate } = this.props;

    if (minDate && !maxDate) {
      return new Date(minDate) < new Date(date);
    } else if (!minDate && maxDate) {
      return new Date(maxDate) > new Date(date);
    } else if (minDate && maxDate) {
      return (
        new Date(minDate) < new Date(date) && new Date(maxDate) > new Date(date)
      );
    }

    return true;
  };

  getLocaleString = () => {
    const { language } = this.props;
    return language ? language.split("-")[0] : Moment.locale();
  };

  getDateFormat = () => {
    const { showTimeSelectOnly, showTimeSelect } = this.props;
    const localeString = this.getLocaleString();
    const localizedTimeFormat = Moment(new Date())
      .localeData(localeString)
      .longDateFormat("LT")
      .toString();

    if (showTimeSelectOnly) {
      return localizedTimeFormat;
    }

    if (showTimeSelect) {
      return `yyyy-MM-dd ${localizedTimeFormat}`;
    }

    if (localeString === "sv") {
      return "yyyy-MM-dd";
    }

    return "MM/dd/yyyy";
  };

  getPlaceholder = () => {
    const { showTimeSelectOnly, showTimeSelect } = this.props;
    let { placeholder } = this.props;
    const localeString = this.getLocaleString();

    // Time Only Placeholder
    if (showTimeSelectOnly) {
      return Moment(new Date())
        .localeData(localeString)
        .longDateFormat("LT")
        .toLowerCase();
    }

    // Calendar
    if (!placeholder) {
      // With Time
      if (showTimeSelect) {
        placeholder =
          Moment(new Date())
            .localeData(localeString)
            .longDateFormat("L")
            .toLowerCase() +
          " " +
          Moment(new Date()).localeData().longDateFormat("LT").toLowerCase();
      } else {
        // Without Time
        placeholder = Moment(new Date())
          .localeData(localeString)
          .longDateFormat("L")
          .toLowerCase();
      }

      // The placeholder is yyyy-mm-dd for Sweden which is incorrect, it should be åååå-mm-dd.
      if (localeString === "sv") {
        placeholder = placeholder.replace(/y/g, "å");
      }
    }

    return placeholder;
  };

  getTimeString = () => {
    const localeString = this.getLocaleString();

    if (localeString === "sv") {
      return "Tid";
    }

    return "Time";
  };

  getDisplayValue = () => {
    const { showTimeSelectOnly, value } = this.props;
    const { rawInputValue } = this.state;

    if (!showTimeSelectOnly) {
      return undefined;
    }

    if (rawInputValue !== null) {
      return rawInputValue;
    }

    if (!value) {
      return undefined;
    }

    const momentValue = Moment(value);

    if (!momentValue.isValid()) {
      return undefined;
    }

    return momentValue.format("HH:mm");
  };

  getMinutesSinceMidnight = (dateValue) => {
    const momentValue = Moment(dateValue);

    if (!momentValue.isValid()) {
      return null;
    }

    return momentValue.hours() * 60 + momentValue.minutes();
  };

  getTimeOnlyBaseMoment = () => {
    const { value, minTime } = this.props;
    const baseMoment = Moment(value || minTime || new Date());

    if (baseMoment.isValid()) {
      return baseMoment.clone();
    }

    return Moment();
  };

  isWithinTimeBounds = (dateValue) => {
    const { minTime } = this.props;
    const candidateMinutes = this.getMinutesSinceMidnight(dateValue);

    if (candidateMinutes === null) {
      return false;
    }

    if (minTime) {
      const minTimeMinutes = this.getMinutesSinceMidnight(minTime);

      if (minTimeMinutes !== null && candidateMinutes < minTimeMinutes) {
        return false;
      }
    }

    return candidateMinutes <= 23 * 60 + 59;
  };

  commitTimeOnlyRawValue = () => {
    const rawValue = this.state.rawInputValue;

    if (rawValue === null) {
      return;
    }

    const trimmedValue = rawValue.trim();

    if (!/^\d{2}:\d{2}$/.test(trimmedValue)) {
      this.setState({ rawInputValue: null });
      return;
    }

    const typedTime = Moment(trimmedValue, "HH:mm", true);

    if (!typedTime.isValid()) {
      this.setState({ rawInputValue: null });
      return;
    }

    const nextValue = this.getTimeOnlyBaseMoment()
      .hours(typedTime.hours())
      .minutes(typedTime.minutes())
      .seconds(0)
      .milliseconds(0);

    if (!this.isWithinTimeBounds(nextValue)) {
      this.setState({ rawInputValue: null });
      return;
    }

    this.onChange(nextValue);
  };

  onBlur = (e) => {
    const { onBlur, showTimeSelectOnly } = this.props;

    if (showTimeSelectOnly) {
      this.commitTimeOnlyRawValue();
    }

    onBlur && onBlur(e);
  };

  // Temporary fix. Should be removed upon 2.7.0 release as there will be
  // way more robust solution
  onChangeRaw = (e) => {
    const { showTimeSelectOnly } = this.props;

    if (showTimeSelectOnly) {
      e.preventDefault();

      this.setState({ rawInputValue: e.target.value });
      return;
    }

    const m = Moment(e.target.value);
    if (m.isValid()) {
      this.onChange(m);
    }
  };

  render = () => {
    const {
      value,
      calendarPlacement,
      disabled,
      isClearable,
      showWeekNumbers,
      showTimeSelect,
      hasError,
      showTimeSelectOnly,
      minTime,
    } = this.props;
    const localeString = this.getLocaleString();

    return (
      <ReactDatePicker
        className="input-field"
        calendarClassName="text-md! bg-transparent"
        popperClassName="shadow-xl"
        hasError={hasError}
        selected={value ? new Date(value) : null}
        onChange={this.onChange}
        placeholderText={this.getPlaceholder()}
        locale={localeString}
        dateFormat={this.getDateFormat()}
        filterDate={this.filterDate}
        popperPlacement={calendarPlacement}
        disabled={disabled}
        isClearable={isClearable}
        value={this.getDisplayValue()}
        timeFormat="HH:mm"
        timeIntervals={15}
        onBlur={this.onBlur}
        timeCaption={this.getTimeString()}
        minTime={minTime && Moment(minTime).toDate()}
        maxTime={minTime && Moment().endOf("day").toDate()}
        showTimeSelect={showTimeSelectOnly ? true : showTimeSelect}
        showTimeSelectOnly={showTimeSelectOnly}
        showWeekNumbers={showWeekNumbers}
        onChangeRaw={this.onChangeRaw}
        showMonthDropdown
        showYearDropdown
      />
    );
  };
}

export default DatePicker;
